import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import RecordButton from "../neutral/RecordButton";
import styles from "./WordBlendFlow.module.css";
import {
  ClusterSpreadCvcBuiltLine,
  InteractiveTapStepRow,
  deriveClusterInteractiveTapSteps,
} from "./clusterSpreadCvc/ClusterSpreadCvc";

function SyncGuideArrowSvg({ className }) {
  const rawId = useId().replace(/:/g, "");
  const gid = `syncGuideArrowGrad-${rawId}`;
  return (
    <svg className={className} viewBox="0 0 100 72" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <linearGradient id={gid} x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%" stopColor="#fef08a" />
          <stop offset="45%" stopColor="#fde047" />
          <stop offset="100%" stopColor="#eab308" />
        </linearGradient>
      </defs>
      <path
        fill="none"
        stroke={`url(#${gid})`}
        strokeWidth="14"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M 14 52 Q 38 14 70 38"
      />
      <path
        fill="#ca8a04"
        stroke="#854d0e"
        strokeWidth="2"
        strokeLinejoin="round"
        d="M 70 24 L 94 38 L 70 52 Z"
      />
    </svg>
  );
}

/**
 * Generic blending UI for lesson prep items.
 *
 * blend.mode === "sync": optional syncNextHandGuideSteps — arrow beside Next + after syncNextAutoAdvanceDelayMs (default 10s) a tap animation and auto-advance on early steps only (bag/tag/rag).
 * blend.mode === "interactive": clusterSpread uses blend.interactiveTapSteps (fed/wed/bed/pit/mud); optional blend.interactiveTapPlain on pit/mud = plain letters + silent taps; spacedPlain legacy path elsewhere. Tap-step flow ends on word + mic.
 */
export default function WordBlendFlow({
  blend,
  registerMicrophone,
  microphone,
  blendDemoApiRef,
  setBlendLessonTimerHidden,
  onMicArm,
  repeatId,
}) {
  const {
    mode,
    word,
    steps = [],
    selectAudio,
    introAudio,
    introWordAudio,
    letterSounds,
    letterTapInlineMic,
    letterTapSkipIntroAudio,
    build,
    merge,
    interactiveBuildUi,
    interactiveTapSteps: interactiveTapStepsJson,
    interactiveTapPlain,
    syncNextHandGuideSteps,
    syncNextAutoAdvanceDelayMs,
  } = blend || {};

  const [syncIndex, setSyncIndex] = useState(0);
  const [phase, setPhase] = useState(() =>
    mode === "sync" ? "sync" : introWordAudio ? "intro" : "build"
  );
  const [buildIdx, setBuildIdx] = useState(0);
  const [mergeIdx, setMergeIdx] = useState(0);
  const [tapStepIndex, setTapStepIndex] = useState(0);
  // Start already unlocked on a retry (remounted with repeatId > 0) so the record step
  // is shown immediately and the countdown re-arms, without replaying the demo.
  const [micUnlocked, setMicUnlocked] = useState(() => repeatId > 0);
  const micUnlockedRef = useRef(repeatId > 0);
  const [pendingAdvance, setPendingAdvance] = useState(false);

  const [syncPendingAdvance, setSyncPendingAdvance] = useState(false);

  const cancelled = useRef(false);
  const runId = useRef(0);
  const onSyncAdvanceRef = useRef(async () => { });
  const tapBusyRef = useRef(false);
  const buildIdxRef = useRef(0);
  const mergeIdxRef = useRef(0);
  const tapStepIndexRef = useRef(0);
  const lettersOrderRevealRef = useRef(0);
  const [lettersOrderRevealCount, setLettersOrderRevealCount] = useState(0);

  const [interactiveTapFedLettersSeen, setInteractiveTapFedLettersSeen] = useState(() => ({}));

  const [letterTapIntroDone, setLetterTapIntroDone] = useState(false);
  const letterTapProgressRef = useRef(0);
  const [letterTapProgressCount, setLetterTapProgressCount] = useState(0);

  const [syncGuidedAutoPressing, setSyncGuidedAutoPressing] = useState(false);

  const order = build?.order || [];
  const mergeChunks = merge?.chunks || [];
  const mergeTap = merge?.tapOrder || mergeChunks;
  const buildSounds = build?.sounds || {};
  const mergeSounds = merge?.sounds || {};

  const interactiveTapSteps = useMemo(() => {
    if (Array.isArray(interactiveTapStepsJson) && interactiveTapStepsJson.length > 0) {
      return interactiveTapStepsJson;
    }
    return deriveClusterInteractiveTapSteps(blend) || [];
  }, [
    interactiveTapStepsJson,
    order.join(","),
    mergeChunks.join(","),
    mergeTap.join(","),
  ]);

  const useInteractiveTapSteps =
    mode === "interactive" &&
    interactiveBuildUi === "clusterSpread" &&
    interactiveTapSteps.length > 0;

  const playInteractiveTapSound =
    interactiveBuildUi !== "spacedPlain" && !interactiveTapPlain;

  const clusterSpreadFedStyleTap =
    useInteractiveTapSteps && interactiveTapSteps[0]?.expect === "letter";

  useEffect(() => {
    cancelled.current = false;
    runId.current += 1;
    setSyncIndex(0);
    setPhase(mode === "sync" ? "sync" : introWordAudio ? "intro" : "build");
    setBuildIdx(0);
    setMergeIdx(0);
    setTapStepIndex(0);
    lettersOrderRevealRef.current = 0;
    setLettersOrderRevealCount(0);
    setLetterTapIntroDone(false);
    letterTapProgressRef.current = 0;
    setLetterTapProgressCount(0);
    // On a retry (repeatId > 0) the component is remounted (keyed by repeatId); skip the
    // demo and open the mic straight away so the record step re-arms cleanly.
    setMicUnlocked(repeatId > 0);
    setPendingAdvance(false);
    setSyncPendingAdvance(false);
    setSyncGuidedAutoPressing(false);
    setInteractiveTapFedLettersSeen({});

    return () => {
      cancelled.current = true;
    };
  }, [mode, word, introWordAudio, introAudio, selectAudio, letterTapSkipIntroAudio]);

  useEffect(() => {
    micUnlockedRef.current = micUnlocked;
  }, [micUnlocked]);

  const micArmedRef = useRef(false);
  useEffect(() => {
    if (micUnlocked && !micArmedRef.current) {
      micArmedRef.current = true;
      onMicArm?.();
    } else if (!micUnlocked) {
      micArmedRef.current = false;
    }
  }, [micUnlocked, onMicArm]);

  useEffect(() => {
    buildIdxRef.current = buildIdx;
  }, [buildIdx]);

  useEffect(() => {
    mergeIdxRef.current = mergeIdx;
  }, [mergeIdx]);

  useEffect(() => {
    tapStepIndexRef.current = tapStepIndex;
  }, [tapStepIndex]);

  useEffect(() => {
    lettersOrderRevealRef.current = 0;
    setLettersOrderRevealCount(0);
  }, [tapStepIndex]);

  const playInstruction = useCallback(async (key) => {
    if (!key) return;
    await window.soundManager.playSound("instruction", key);
  }, []);

  const playBlendTapSound = useCallback(async (key) => {
    if (!key) return;
    await window.soundManager.playSound("blendFx", key);
  }, []);

  useEffect(() => {
    if (mode !== "sync" || !steps.length || micUnlocked) return;
    let localId = runId.current;
    setSyncPendingAdvance(false);
    (async () => {
      const a = steps[syncIndex]?.audio;
      if (a) await playInstruction(a).catch(() => { });
      if (cancelled.current || localId !== runId.current) return;
      setSyncPendingAdvance(true);
    })();
  }, [mode, steps, syncIndex, micUnlocked, playInstruction]);

  useEffect(() => {
    if (!setBlendLessonTimerHidden) return undefined;
    const hideUntilMic =
      (mode === "sync" || mode === "interactive" || mode === "letterTap") && !micUnlocked;
    setBlendLessonTimerHidden(hideUntilMic);
    return () => setBlendLessonTimerHidden(false);
  }, [mode, micUnlocked, setBlendLessonTimerHidden]);

  useEffect(() => {
    const syncNextHandGuideMax =
      typeof syncNextHandGuideSteps === "number" && syncNextHandGuideSteps > 0
        ? syncNextHandGuideSteps
        : 0;
    const guidedAutoDelayMs =
      syncNextHandGuideMax > 0
        ? typeof syncNextAutoAdvanceDelayMs === "number"
          ? syncNextAutoAdvanceDelayMs
          : 10000
        : 0;

    if (mode !== "sync" || !syncPendingAdvance || micUnlocked) return;
    if (!(syncNextHandGuideMax > 0 && syncIndex < syncNextHandGuideMax && guidedAutoDelayMs > 0))
      return;

    let stale = false;
    let pressTimer = null;
    const waitTimer = window.setTimeout(() => {
      if (stale || cancelled.current || micUnlockedRef.current) return;
      setSyncGuidedAutoPressing(true);
      pressTimer = window.setTimeout(() => {
        if (stale || cancelled.current || micUnlockedRef.current) return;
        setSyncGuidedAutoPressing(false);
        void Promise.resolve().then(() => onSyncAdvanceRef.current?.());
      }, 560);
    }, guidedAutoDelayMs);

    return () => {
      stale = true;
      window.clearTimeout(waitTimer);
      if (pressTimer != null) window.clearTimeout(pressTimer);
      setSyncGuidedAutoPressing(false);
    };
  }, [
    mode,
    syncPendingAdvance,
    micUnlocked,
    syncIndex,
    syncNextHandGuideSteps,
    syncNextAutoAdvanceDelayMs,
  ]);

  useEffect(() => {
    if (mode !== "interactive") return;
    let localId = runId.current;
    (async () => {
      if (introWordAudio) {
        await playInstruction(introWordAudio);
        if (cancelled.current || localId !== runId.current) return;
      }
      const startTapFlow = interactiveBuildUi === "clusterSpread" && interactiveTapSteps.length > 0;
      setPhase(startTapFlow ? "interactiveTap" : "build");
    })();
  }, [
    mode,
    word,
    introWordAudio,
    playInstruction,
    interactiveBuildUi,
    interactiveTapSteps,
  ]);

  useEffect(() => {
    if (mode !== "letterTap" || !word) return;
    let localId = runId.current;
    setLetterTapIntroDone(false);
    if (letterTapSkipIntroAudio) {
      setLetterTapIntroDone(true);
      return;
    }
    const intro = introAudio || selectAudio || "blend/select";
    (async () => {
      await playInstruction(intro);
      if (cancelled.current || localId !== runId.current) return;
      setLetterTapIntroDone(true);
    })();
  }, [mode, word, introAudio, selectAudio, letterTapSkipIntroAudio, playInstruction]);

  const soundKeyForLetter = useCallback(
    (ch) => {
      const lower = String(ch).toLowerCase();
      const map = letterSounds || {};
      if (map[lower]) return map[lower];
      return `blend/l4-${lower}`;
    },
    [letterSounds]
  );

  const mergeSoundKey = useCallback(
    (chunk) => {
      if (mergeSounds[chunk]) return mergeSounds[chunk];
      const s = String(chunk);
      if (/^[a-zA-Z]$/.test(s)) return soundKeyForLetter(s);
      return `blend/l4-${s.toLowerCase()}`;
    },
    [mergeSounds, soundKeyForLetter]
  );

  const skipDemoToMic = useCallback(() => {
    cancelled.current = true;
    runId.current += 1;
    try {
      void window.soundManager?.cleanChannel?.("instruction");
      void window.soundManager?.cleanChannel?.("blendFx");
    } catch (_) {
      /* ignore */
    }
    if (mode === "sync" && steps.length) {
      setSyncIndex(Math.max(0, steps.length - 1));
    } else if (mode === "interactive") {
      if (!(interactiveTapSteps.length > 0 && interactiveBuildUi === "clusterSpread")) {
        setPhase("wholeWord");
        setPendingAdvance(false);
      }
    } else if (mode === "letterTap" && word) {
      const taps = String(word)
        .split("")
        .filter((c) => /[a-zA-Z]/.test(c));
      setLetterTapIntroDone(true);
      letterTapProgressRef.current = taps.length;
      setLetterTapProgressCount(taps.length);
      setPendingAdvance(false);
    }
    setSyncPendingAdvance(false);
    micUnlockedRef.current = true;
    setMicUnlocked(true);
  }, [mode, steps, interactiveTapSteps, interactiveBuildUi, word]);

  useEffect(() => {
    if (!blendDemoApiRef) return undefined;
    blendDemoApiRef.current = {
      isMicUnlocked: () => micUnlockedRef.current,
      skipDemoToMic,
    };
    return () => {
      blendDemoApiRef.current = null;
    };
  }, [blendDemoApiRef, skipDemoToMic]);

  const onLetterTap = async (letter) => {
    if (phase !== "build" || micUnlocked || tapBusyRef.current) return;
    tapBusyRef.current = true;
    try {
      const expect = order[buildIdxRef.current];
      const correctTap = letter === expect;
      if (interactiveBuildUi !== "spacedPlain") {
        const key = buildSounds[letter] ?? soundKeyForLetter(letter);
        await playBlendTapSound(key).catch(() => { });
      }
      if (correctTap) {
        setPendingAdvance(true);
      }
    } finally {
      tapBusyRef.current = false;
    }
  };

  const onBuildAdvance = () => {
    if (!pendingAdvance || phase !== "build" || micUnlocked) return;
    setPendingAdvance(false);
    const next = buildIdx + 1;
    setBuildIdx(next);
    if (next >= order.length) {
      setPhase(mergeTap.length > 0 ? "merge" : "wholeWord");
    }
  };

  const onMergeTap = async (chunk) => {
    if (phase !== "merge" || micUnlocked || tapBusyRef.current) return;
    tapBusyRef.current = true;
    try {
      const expect = mergeTap[mergeIdxRef.current];
      const correctTap = chunk === expect;
      const key = mergeSoundKey(chunk);
      await playBlendTapSound(key).catch(() => { });
      if (correctTap) {
        setPendingAdvance(true);
      }
    } finally {
      tapBusyRef.current = false;
    }
  };

  const onMergeAdvance = () => {
    if (!pendingAdvance || phase !== "merge" || micUnlocked) return;
    setPendingAdvance(false);
    const next = mergeIdx + 1;
    setMergeIdx(next);
    if (next >= mergeTap.length) {
      setPhase("wholeWord");
    }
  };

  const onInteractiveTapPayload = async (payload) => {
    if (phase !== "interactiveTap" || micUnlocked || tapBusyRef.current) return;
    const step = interactiveTapSteps[tapStepIndexRef.current];
    if (!step) return;
    tapBusyRef.current = true;
    try {
      if (!clusterSpreadFedStyleTap) {
        // Cluster (lesson 5) flow: every step is "tap each displayed part left-to-right".
        // Each correct in-order tap reveals that part, so the word builds up across the
        // whole step (gl -> gl a -> gl a d, then gl -> gl ad), and we advance once the
        // last part is tapped. Works for letter pills (clusterEqual/Bridge) and chunk
        // pills (mergeWide) alike, since we match on the tapped value's position.
        const seq =
          step.expect === "lettersInOrder" && Array.isArray(step.targets) && step.targets.length
            ? step.targets
            : step.chunks || [];
        const idx = lettersOrderRevealRef.current;
        if (idx < seq.length && payload.value === seq[idx]) {
          const nextIdx = idx + 1;
          lettersOrderRevealRef.current = nextIdx;
          setLettersOrderRevealCount(nextIdx);
          if (nextIdx >= seq.length) setPendingAdvance(true);
          if (playInteractiveTapSound) {
            const v = payload.value;
            const key =
              payload.kind === "chunk"
                ? mergeSoundKey(v)
                : buildSounds[v] ?? soundKeyForLetter(v);
            await playBlendTapSound(key).catch(() => { });
          }
        }
        return;
      }

      // Lesson 4 fed/wed/bed flow (first step taps a single letter): keep accumulating the
      // seen-letters map so the whole word fills in across the steps.
      let correct = false;
      if (step.expect === "lettersInOrder") {
        const targets = step.targets;
        if (Array.isArray(targets) && targets.length > 0) {
          const idx = lettersOrderRevealRef.current;
          correct = payload.kind === "letter" && payload.value === targets[idx];
          if (correct) {
            const nextIdx = idx + 1;
            lettersOrderRevealRef.current = nextIdx;
            setLettersOrderRevealCount(nextIdx);
            if (nextIdx >= targets.length) setPendingAdvance(true);
            if (playInteractiveTapSound) {
              const letter = payload.value;
              const key = buildSounds[letter] ?? soundKeyForLetter(letter);
              await playBlendTapSound(key).catch(() => { });
            }
          }
        }
      } else if (step.expect === "letter") {
        correct = payload.kind === "letter" && payload.value === step.target;
        if (correct) setPendingAdvance(true);
        if (payload.kind === "letter" && word) {
          const key = String(payload.value).toLowerCase();
          if (/^[a-z]$/i.test(key) && word.toLowerCase().includes(key)) {
            setInteractiveTapFedLettersSeen((prev) =>
              prev[key] ? prev : { ...prev, [key]: true }
            );
          }
        }
        if (playInteractiveTapSound) {
          const letter = payload.value;
          const key = buildSounds[letter] ?? soundKeyForLetter(letter);
          await playBlendTapSound(key).catch(() => { });
        }
      } else if (step.expect === "chunk") {
        correct = payload.kind === "chunk" && payload.value === step.target;
        if (correct) {
          setPendingAdvance(true);
          if (word) {
            const next = {};
            for (const ch of word) {
              if (/[a-zA-Z]/.test(ch)) next[ch.toLowerCase()] = true;
            }
            setInteractiveTapFedLettersSeen((prev) => ({ ...prev, ...next }));
          }
        }
        if (payload.kind === "chunk" && playInteractiveTapSound) {
          const key = mergeSoundKey(payload.value);
          await playBlendTapSound(key).catch(() => { });
        }
      }
    } finally {
      tapBusyRef.current = false;
    }
  };

  const onInteractiveTapAdvance = () => {
    if (!pendingAdvance || phase !== "interactiveTap" || micUnlocked) return;
    setPendingAdvance(false);
    const next = tapStepIndex + 1;
    if (next >= interactiveTapSteps.length) {
      micUnlockedRef.current = true;
      setMicUnlocked(true);
      if (selectAudio) void playInstruction(selectAudio).catch(() => { });
    } else {
      setInteractiveTapFedLettersSeen({});
      setTapStepIndex(next);
    }
  };

  const onWholeWordTap = async () => {
    if (phase !== "wholeWord" || micUnlocked || tapBusyRef.current) return;
    tapBusyRef.current = true;
    try {
      if (selectAudio) await playInstruction(selectAudio).catch(() => { });
      setPendingAdvance(true);
    } finally {
      tapBusyRef.current = false;
    }
  };

  const onWholeWordAdvance = () => {
    if (!pendingAdvance || phase !== "wholeWord" || micUnlocked) return;
    setPendingAdvance(false);
    setMicUnlocked(true);
  };

  const micProps = registerMicrophone(microphone ?? true);

  const onSyncAdvance = async () => {
    if (!syncPendingAdvance || mode !== "sync" || micUnlocked) return;
    setSyncPendingAdvance(false);
    const next = syncIndex + 1;
    if (next >= steps.length) {
      if (selectAudio) await playInstruction(selectAudio).catch(() => { });
      setMicUnlocked(true);
    } else {
      setSyncIndex(next);
    }
  };

  onSyncAdvanceRef.current = onSyncAdvance;

  const onLetterSoundTap = async (ch) => {
    if (mode !== "letterTap" || micUnlocked || !letterTapIntroDone || tapBusyRef.current) return;
    const taps =
      word &&
      String(word)
        .split("")
        .filter((c) => /[a-zA-Z]/.test(c));
    if (!taps?.length) return;
    const idx = letterTapProgressRef.current;
    const expect = taps[idx];
    if (String(ch).toLowerCase() !== String(expect).toLowerCase()) return;
    tapBusyRef.current = true;
    try {
      await playInstruction(soundKeyForLetter(ch)).catch(() => { });
      const next = idx + 1;
      letterTapProgressRef.current = next;
      setLetterTapProgressCount(next);
      if (next >= taps.length) {
        if (letterTapInlineMic) {
          micUnlockedRef.current = true;
          setMicUnlocked(true);
        } else {
          setPendingAdvance(true);
        }
      }
    } finally {
      tapBusyRef.current = false;
    }
  };

  const onLetterTapAdvance = () => {
    if (!pendingAdvance || mode !== "letterTap" || micUnlocked) return;
    setPendingAdvance(false);
    micUnlockedRef.current = true;
    setMicUnlocked(true);
    if (selectAudio) void playInstruction(selectAudio).catch(() => { });
  };

  const letters =
    mode === "letterTap" && word
      ? String(word)
        .split("")
        .filter((c) => /[a-zA-Z]/.test(c))
      : [];

  const showLetterTapMicSlot =
    mode === "letterTap" &&
    Boolean(letterTapInlineMic) &&
    letterTapIntroDone &&
    letters.length > 0;

  const currentStep = mode === "sync" ? steps[syncIndex] : null;
  const layout = currentStep?.layout || "normal";

  const showHeadingWord =
    mode !== "letterTap" && mode !== "sync" && mode !== "interactive";

  const showInteractivePanels = mode === "interactive" && !micUnlocked;

  const buildHint = useInteractiveTapSteps
    ? "Tap each part in order, then press Next."
    : interactiveBuildUi === "spacedPlain"
      ? "Tap each letter in order, then press Next."
      : interactiveBuildUi === "clusterSpread"
        ? "Tap each sound in order, then press Next."
        : "Tap each sound in order, then press Next.";

  const mergePlain = interactiveBuildUi === "spacedPlain";

  const syncNextHandGuideMax =
    typeof syncNextHandGuideSteps === "number" && syncNextHandGuideSteps > 0
      ? syncNextHandGuideSteps
      : 0;
  const guidedAutoDelayMs =
    syncNextHandGuideMax > 0
      ? typeof syncNextAutoAdvanceDelayMs === "number"
        ? syncNextAutoAdvanceDelayMs
        : 10000
      : 0;

  const showSyncNextHandGuide =
    mode === "sync" &&
    syncPendingAdvance &&
    syncNextHandGuideMax > 0 &&
    syncIndex < syncNextHandGuideMax;

  const finalSyncStep = mode === "sync" && steps.length ? steps[steps.length - 1] : null;
  const finalSyncLayout = finalSyncStep?.layout || "normal";

  const buildRevealLen =
    phase === "build"
      ? Math.min(order.length, buildIdx + (pendingAdvance ? 1 : 0))
      : 0;

  const mergeRevealLen =
    phase === "merge"
      ? Math.min(mergeTap.length, mergeIdx + (pendingAdvance ? 1 : 0))
      : 0;

  let interactiveTapPreviewText = "";
  if (phase === "interactiveTap" && word) {
    if (clusterSpreadFedStyleTap) {
      const seen = interactiveTapFedLettersSeen;
      interactiveTapPreviewText = Array.from(String(word)).map((ch) =>
        /[a-zA-Z]/.test(ch) && seen[ch.toLowerCase()] ? ch : ""
      ).join("");
    } else {
      // Cluster (lesson 5) flow: the word builds from the parts tapped so far in this step,
      // in display order (matches the tap handler's `seq`).
      const cur = interactiveTapSteps[tapStepIndex];
      const seq = cur
        ? cur.expect === "lettersInOrder" && Array.isArray(cur.targets) && cur.targets.length
          ? cur.targets
          : cur.chunks || []
        : [];
      interactiveTapPreviewText = seq.slice(0, lettersOrderRevealCount).join("");
    }
  }

  return (
    <div className={styles.wrap}>
      {showHeadingWord && (
        <div className={styles.wordTarget} aria-hidden>
          {word}
        </div>
      )}

      {mode === "sync" && currentStep && !micUnlocked && (
        <>
          <div className={styles.subLabel}>Listen, then press Next.</div>
          <div
            className={clsx(
              styles.chunksRow,
              layout === "spread" && styles.spread,
              layout === "bridge" && styles.bridge,
              layout === "tight" && styles.tight,
              layout === "word" && styles.asWord
            )}
          >
            {currentStep.chunks.map((c, i) => (
              <span key={`${syncIndex}-${i}-${c}`} className={styles.chunk}>
                {c}
              </span>
            ))}
          </div>
          {syncPendingAdvance && (
            <div className={styles.advanceBtnWithGuide}>
              {showSyncNextHandGuide && (
                <span
                  className={clsx(
                    styles.nextHandGuide,
                    !syncGuidedAutoPressing && styles.nextHandGuideIdle,
                    syncGuidedAutoPressing && styles.nextHandGuideTap
                  )}
                  aria-hidden
                >
                  <SyncGuideArrowSvg className={styles.nextGuideArrowSvg} />
                </span>
              )}
              <button
                type="button"
                className={clsx(styles.advanceBtn, syncGuidedAutoPressing && styles.advanceBtnGuidedPress)}
                onClick={onSyncAdvance}
                aria-label="Next blending step"
              >
                <span className={styles.advanceArrow} aria-hidden>
                  →
                </span>
                <span className={styles.advanceLabel}>Next</span>
              </button>
            </div>
          )}
        </>
      )}

      {mode === "sync" && micUnlocked && finalSyncStep && (
        <>
          <div
            className={clsx(
              styles.chunksRow,
              finalSyncLayout === "spread" && styles.spread,
              finalSyncLayout === "bridge" && styles.bridge,
              finalSyncLayout === "tight" && styles.tight,
              finalSyncLayout === "word" && styles.asWord
            )}
          >
            {finalSyncStep.chunks.map((c, i) => (
              <span key={`sync-final-${i}-${c}`} className={styles.chunk}>
                {c}
              </span>
            ))}
          </div>
        </>
      )}

      {mode === "letterTap" && letters.length > 0 && !micUnlocked && (
        <>
          <div className={styles.subLabel}>
            {!letterTapIntroDone
              ? "Listen, then tap each letter in order."
              : letterTapInlineMic
                ? "Tap each letter in order."
                : "Tap each letter in order, then press Next."}
          </div>
          <div className={clsx(styles.chunksRow, styles.spread)}>
            {letters.map((L, idx) => (
              <button
                key={`lt-${idx}-${L}`}
                type="button"
                className={clsx(styles.chunk, styles.chunkBtn)}
                disabled={!letterTapIntroDone}
                onClick={() => onLetterSoundTap(L)}
              >
                {L}
              </button>
            ))}
          </div>
          <div className={styles.builtRow}>
            {letters.slice(0, letterTapProgressCount).join("")}
          </div>
          {pendingAdvance && !letterTapInlineMic && (
            <button
              type="button"
              className={styles.advanceBtn}
              onClick={onLetterTapAdvance}
              aria-label="Show word and record"
            >
              <span className={styles.advanceArrow} aria-hidden>
                →
              </span>
              <span className={styles.advanceLabel}>Next</span>
            </button>
          )}
        </>
      )}

      {showInteractivePanels && phase === "interactiveTap" && interactiveTapSteps[tapStepIndex] && (
        <>
          <div className={styles.subLabel}>{buildHint}</div>
          <InteractiveTapStepRow
            step={interactiveTapSteps[tapStepIndex]}
            onTapPayload={onInteractiveTapPayload}
            plainUi={Boolean(interactiveTapPlain)}
          />
          <ClusterSpreadCvcBuiltLine text={interactiveTapPreviewText} />
          {pendingAdvance && (
            <button
              type="button"
              className={styles.advanceBtn}
              onClick={onInteractiveTapAdvance}
              aria-label="Next step"
            >
              <span className={styles.advanceArrow} aria-hidden>
                →
              </span>
              <span className={styles.advanceLabel}>Next</span>
            </button>
          )}
        </>
      )}

      {showInteractivePanels && phase === "build" && !useInteractiveTapSteps && (
        <>
          <div className={styles.subLabel}>{buildHint}</div>
          <div
            className={clsx(
              interactiveBuildUi === "spacedPlain" && styles.spacedPlainRow,
              interactiveBuildUi !== "spacedPlain" && styles.chunksRow,
              interactiveBuildUi !== "spacedPlain" && styles.spread
            )}
          >
            {order.map((L, idx) => (
              <button
                key={`${idx}-${L}`}
                type="button"
                className={clsx(
                  interactiveBuildUi === "spacedPlain" && styles.spacedPlainLetter,
                  interactiveBuildUi !== "spacedPlain" && styles.chunk,
                  interactiveBuildUi !== "spacedPlain" && styles.chunkBtn
                )}
                onClick={() => onLetterTap(L)}
              >
                {L}
              </button>
            ))}
          </div>
          <div className={styles.builtRow}>{order.slice(0, buildRevealLen).join("")}</div>
          {pendingAdvance && (
            <button
              type="button"
              className={styles.advanceBtn}
              onClick={onBuildAdvance}
              aria-label="Next step"
            >
              <span className={styles.advanceArrow} aria-hidden>
                →
              </span>
              <span className={styles.advanceLabel}>Next</span>
            </button>
          )}
        </>
      )}

      {showInteractivePanels && phase === "merge" && mergeTap.length > 0 && !useInteractiveTapSteps && (
        <>
          <div className={styles.subLabel}>
            Tap each part in order, then press Next.
          </div>
          <div
            className={clsx(
              mergePlain ? styles.spacedPlainRow : styles.chunksRow,
              !mergePlain && styles.spread
            )}
          >
            {mergeChunks.map((c) => (
              <button
                key={c}
                type="button"
                className={clsx(
                  mergePlain ? styles.spacedPlainLetter : styles.chunk,
                  !mergePlain && styles.chunkBtn
                )}
                onClick={() => onMergeTap(c)}
              >
                {c}
              </button>
            ))}
          </div>
          <div className={styles.builtRow}>
            {mergeTap.slice(0, mergeRevealLen).join("")}
          </div>
          {pendingAdvance && (
            <button
              type="button"
              className={styles.advanceBtn}
              onClick={onMergeAdvance}
              aria-label="Next step"
            >
              <span className={styles.advanceArrow} aria-hidden>
                →
              </span>
              <span className={styles.advanceLabel}>Next</span>
            </button>
          )}
        </>
      )}

      {showInteractivePanels && phase === "wholeWord" && word && !useInteractiveTapSteps && (
        <>
          <div className={styles.subLabel}>Tap the word, then press Next to record.</div>
          <button type="button" className={styles.wholeWordPlain} onClick={onWholeWordTap}>
            {word}
          </button>
          {pendingAdvance && (
            <button
              type="button"
              className={styles.advanceBtn}
              onClick={onWholeWordAdvance}
              aria-label="Open microphone"
            >
              <span className={styles.advanceArrow} aria-hidden>
                →
              </span>
              <span className={styles.advanceLabel}>Next</span>
            </button>
          )}
        </>
      )}

      {(micUnlocked || showLetterTapMicSlot) && (
        <>
          {micUnlocked && mode === "interactive" && interactiveBuildUi === "clusterSpread" && word && (
            <div className={clsx(styles.wordTarget, styles.wordTargetBlendMicFinal)} aria-hidden>
              {word}
            </div>
          )}
          {micUnlocked && mode === "letterTap" && word && (
            <div className={clsx(styles.wordTarget, styles.wordTargetBlendMicFinal)} aria-hidden>
              {word}
            </div>
          )}
          <div className={styles.micRow}>
            <RecordButton
              {...micProps}
              contextuallyDisabled={false}
            />
          </div>
        </>
      )}
    </div>
  );
}
